import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { Box, Stack } from '@mui/material';
import { AlertCard } from 'common/ui/AlertCard';
import { Loading } from 'components/Loading';
import { Form, FormRef, FormOption } from 'common/form';
import { Button } from 'system/atoms';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { pathsV2Creators } from 'core/services/navigation/usePathsV2';
import {
  useSearchCandidateProfileListsLazyQuery,
  useAddCandidateProfilesToListMutation,
  useHasCandidateProfileListsQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { SearchCandidateProfileListsDocument } from 'generated-graphql/graphql';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { buildTextSearchClauses } from 'utils/buildTextSearchClauses';
import { addToTalentListDrawerSx } from 'modules/candidate-profile/styles/addToTalentListDrawerSx';
import {
  selectTalentPoolSchema,
  SelectTalentPoolFormData
} from 'modules/candidate-profile/forms/schema';
import { getSelectTalentPoolFields } from 'modules/candidate-profile/forms/fields';
import { useCreateTalentPoolModal } from 'modules/candidate-profile/hooks/useCreateTalentPoolModal';
import { useDuplicateCandidateProfileListMembers } from 'modules/candidate-profile/hooks/useDuplicateCandidateProfileListMembers';
import { useToast } from 'services/toast/hooks';
import { ToastType } from 'services/toast';
import { TalentPoolCreationToastMessage } from 'modules/candidate-profile/components/TalentPoolCreationToastMessage';
import { waitForOpenSearchAndWeaviate } from 'modules/candidate-profile/utils/waitForOpenSearchAndWeaviate';
import { getAtsEmbedContext } from 'modules/atsEmbed/utils/atsEmbedContext';

type AddToTalentListDrawerContentProps = {
  selectedCandidateIds: string[];
  onAddToList?: () => Promise<void>;
};

export const AddToTalentListDrawerContent: React.FC<
  AddToTalentListDrawerContentProps
> = ({ selectedCandidateIds, onAddToList }) => {
  const client = useApolloClient();
  const navigate = useNavigate();
  const formRef = useRef<FormRef<SelectTalentPoolFormData>>(null);
  const { closeDrawer } = useDrawerV2();
  const organization = useOrganization();
  const { showToast } = useToast();
  const [talentListOptions, setTalentListOptions] = useState<FormOption[]>([]);
  const [selectedListId, setSelectedListId] = useState<string | null>(null);

  const [searchTalentLists] = useSearchCandidateProfileListsLazyQuery();
  const [isAddingToList, setIsAddingToList] = useState(false);
  const [addCandidateProfilesToList] = useAddCandidateProfilesToListMutation();

  const { data: hasTalentListsData, loading: isCheckingTalentLists } =
    useHasCandidateProfileListsQuery({
      variables: { organizationId: organization.id }
    });

  const hasTalentLists =
    (hasTalentListsData?.searchCandidateProfileListsForUser?.total ?? 0) > 0;

  const {
    duplicateIds,
    nonDuplicateIds,
    isLoading: isCheckingDuplicates
  } = useDuplicateCandidateProfileListMembers({
    candidateProfileIds: selectedCandidateIds,
    listId: selectedListId
  });

  // Inside Bullhorn the recruiter stays on the search results.
  const openTalentList = (talentPoolId: string) => {
    if (getAtsEmbedContext()) return;
    navigate(pathsV2Creators.TALENT_POOL_DETAILS({ talentPoolId }));
  };

  // The form only mounts once the has-lists check is done, so subscribe then.
  useEffect(() => {
    const subscription = formRef.current?.watch(
      (value: SelectTalentPoolFormData) => {
        setSelectedListId(value.talentPoolId ?? null);
      }
    );

    return () => subscription?.unsubscribe();
  }, [isCheckingTalentLists, hasTalentLists]);

  const handleSuccess = async () => {
    const talentPoolId = formRef.current?.getValues().talentPoolId;
    const selectedTalentList = talentListOptions.find(
      (opt) => opt.value === talentPoolId
    );

    if (!talentPoolId || !selectedTalentList) return;

    if (nonDuplicateIds.length === 0) {
      showToast({
        title: 'All candidates are already in the list',
        type: ToastType.Info
      });
      return;
    }

    setIsAddingToList(true);

    await addCandidateProfilesToList({
      variables: {
        input: {
          candidateProfileIds: nonDuplicateIds,
          candidateProfileListId: talentPoolId
        }
      }
    });

    await waitForOpenSearchAndWeaviate();

    const toastTitle: string =
      nonDuplicateIds.length === 1
        ? 'Candidate added to talent list'
        : `${nonDuplicateIds.length} candidates added to talent list`;

    await onAddToList?.();

    showToast({
      title: toastTitle,
      message: (
        <TalentPoolCreationToastMessage
          talentPoolTitle={selectedTalentList.label as string}
          talentPoolId={talentPoolId}
        />
      ),
      type: ToastType.Success
    });
    closeDrawer();
    setIsAddingToList(false);

    openTalentList(talentPoolId);

    // Wait for the drawer to close before refetching the talent lists
    // to avoid race condition where an toast is incorrectly shown.
    setTimeout(async () => {
      await client.refetchQueries({
        include: [SearchCandidateProfileListsDocument]
      });
    }, 100);
  };

  const handleCreatePoolSuccess = async (
    newTalentPoolId: string,
    newTalentPoolTitle: string
  ) => {
    setIsAddingToList(true);

    await addCandidateProfilesToList({
      variables: {
        input: {
          candidateProfileIds: selectedCandidateIds,
          candidateProfileListId: newTalentPoolId
        }
      }
    });

    await waitForOpenSearchAndWeaviate();

    await client.refetchQueries({
      include: [SearchCandidateProfileListsDocument]
    });

    const toastTitle =
      selectedCandidateIds.length === 1
        ? 'Candidate added to talent list'
        : `${selectedCandidateIds.length} candidates added to talent list`;

    await onAddToList?.();

    showToast({
      title: toastTitle,
      message: (
        <TalentPoolCreationToastMessage
          talentPoolTitle={newTalentPoolTitle}
          talentPoolId={newTalentPoolId}
        />
      ),
      type: ToastType.Success
    });
    setIsAddingToList(false);
    closeDrawer();

    openTalentList(newTalentPoolId);
  };

  const { openCreateTalentPoolModal } = useCreateTalentPoolModal({
    onSuccess: handleCreatePoolSuccess
  });

  const onSearchFilter = async (query: string) => {
    const { data } = await searchTalentLists({
      variables: {
        organizationId: organization.id,
        filter: query
          ? JSON.stringify({ or: buildTextSearchClauses('title', query) })
          : undefined,
        limit: 10
      }
    });

    const talentLists = extractQueryListItems(data);
    const options = talentLists.map((list) => ({
      label: list.title ?? '',
      subLabel: `${list.candidateProfilesCount} candidate${
        list.candidateProfilesCount !== 1 ? 's' : ''
      }`,
      value: list.id
    }));
    // Keep lists from earlier searches, so a picked list still has its name.
    setTalentListOptions((previous) => [
      ...previous.filter(
        (option) => !options.some(({ value }) => value === option.value)
      ),
      ...options
    ]);
    return options;
  };

  const fields = getSelectTalentPoolFields({ onSearchFilter });

  const getAlertCardTitle = (): string => {
    const talentPoolId = formRef.current?.getValues().talentPoolId;
    const selectedTalentList = talentListOptions.find(
      (opt) => opt.value === talentPoolId
    );
    const listName = selectedTalentList
      ? `the ${selectedTalentList.label}`
      : 'this';

    if (nonDuplicateIds.length === 0) {
      if (duplicateIds.length === 1) {
        return `Candidate is already in ${listName} list`;
      }
      return `All candidates are already in ${listName} list`;
    }

    return `${duplicateIds.length} selected candidate${
      duplicateIds.length !== 1 ? 's are' : ' is'
    } already in ${listName} list`;
  };

  if (isCheckingTalentLists) {
    return (
      <Stack alignItems="center" justifyContent="center" py="60px">
        <Loading size="40px" />
      </Stack>
    );
  }

  if (!hasTalentLists) {
    return (
      <Box sx={addToTalentListDrawerSx.container}>
        <Stack direction="row" gap="12px" justifyContent="flex-start">
          <Button variant="primary" onClick={openCreateTalentPoolModal}>
            Create new talent list
          </Button>
          <Button variant="tertiary" onClick={closeDrawer}>
            Cancel
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={addToTalentListDrawerSx.container}>
      <Form
        ref={formRef}
        schema={selectTalentPoolSchema}
        fields={fields}
        onSubmit={handleSuccess}
        onActionButtonClick={openCreateTalentPoolModal}
        onCancel={closeDrawer}
        submitButtonProps={{
          label: 'Confirm',
          loadingType: 'bar',
          loading: isAddingToList || isCheckingDuplicates
        }}
        actionButtonProps={{ label: 'Create new talent list' }}
        cancelButtonProps={{ label: 'Cancel' }}
      />

      {isCheckingDuplicates && (
        <Stack alignItems="center">
          <Loading size="24px" />
        </Stack>
      )}

      {!isCheckingDuplicates && duplicateIds.length > 0 && (
        <AlertCard type="warning" title={getAlertCardTitle()}>
          They won't be added again if you choose this list.
        </AlertCard>
      )}
    </Box>
  );
};
